import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from './useAuth';

export interface AdminIssue {
  id: string;
  title: string;
  description?: string;
  type: 'idea' | 'bug' | 'todo';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'not_started' | 'in_progress' | 'resolved' | 'archived';
  page_url: string;
  page_title?: string;
  user_agent?: string;
  created_by: string;
  created_by_email: string;
  assigned_to?: string;
  assigned_to_email?: string;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  tags?: string[];
  attachments?: string[];
  screenshot_url?: string;
  error_log?: any;
}

export interface IssueFilters {
  status?: string;
  type?: string;
  createdBy?: string;
}

export const useAdminIssues = (filters?: IssueFilters) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: issues, isLoading } = useQuery({
    queryKey: ['admin-issues', filters],
    queryFn: async () => {
      let query = supabase
        .from('admin_issues')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (filters?.status) {
        query = query.eq('status', filters.status as any);
      }
      if (filters?.type) {
        query = query.eq('type', filters.type as any);
      }
      if (filters?.createdBy) {
        query = query.eq('created_by', filters.createdBy);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as AdminIssue[];
    },
    enabled: !!user,
  });

  const createIssueMutation = useMutation({
    mutationFn: async (newIssue: any) => {
      const { data, error } = await supabase
        .from('admin_issues')
        .insert([newIssue])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-issues'] });
      toast.success('Issue oprettet!');
    },
    onError: (error) => {
      console.error('Error creating issue:', error);
      toast.error('Kunne ikke oprette issue');
    },
  });

  const updateIssueMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<AdminIssue> }) => {
      const { data, error } = await supabase
        .from('admin_issues')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-issues'] });
      toast.success('Issue opdateret!');
    },
    onError: (error) => {
      console.error('Error updating issue:', error);
      toast.error('Kunne ikke opdatere issue');
    },
  });

  const deleteIssueMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('admin_issues')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-issues'] });
      toast.success('Issue slettet!');
    },
    onError: (error) => {
      console.error('Error deleting issue:', error);
      toast.error('Kunne ikke slette issue');
    },
  });

  return {
    issues: issues || [],
    isLoading,
    createIssue: createIssueMutation.mutate,
    updateIssue: updateIssueMutation.mutate,
    deleteIssue: deleteIssueMutation.mutate,
  };
};
